import * as React from "react";
import { AlertColor, Card, Stack, Typography } from "@mui/material";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../layouts/standard";
import {
    useEnableDocumentMutation,
    useDisableDocumentMutation,
    useGetAllDocumentsQuery,
    useDeleteDocumentMutation,
} from "../../services/cclTokenedAssessmentApi";
import DocumentsDataGridPro from "./documentsDataGrid";
import { DocumentItem } from "../../services/types/assessmentAPITypes";
import CclAlertSnackbar from "../../components/common/cclAlertSnackbar";
import { AssetDownloadRequest } from "../../services/types/assetApiTypes";
import { FileDownloadService } from "../../services/fileDownloadService/fileDownloadService";
import DocumentUploadDrawer from "./documentUploadDrawer";
import EditDocumentDrawer from "./editDocumentDrawer";
import useLogAccessEvent from "../../hooks/useLogAccessEvent";
import { AccessEventDocumentDetails } from "../../services/types/accessEventTypes";

export const Documents: React.FC = () => {
    const downloadService = new FileDownloadService();
    const { logEvent } = useLogAccessEvent();

    const { data: documents, isLoading } = useGetAllDocumentsQuery();
    const [documentData, setDocumentData] = React.useState<DocumentItem[]>([]);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [enableDocument] = useEnableDocumentMutation();
    const [disableDocument] = useDisableDocumentMutation();
    const [deleteDocument] = useDeleteDocumentMutation();
    const [deleteInProgress, setDeleteInProgress] = React.useState<boolean>(false);
    const [uploadDrawerOpen, setUploadDrawerOpen] = React.useState<boolean>(false);
    const [enableDisableInProgress, setEnableDisableInProgress] = React.useState<boolean>(false);
    const [editDrawerOpen, setEditDrawerOpen] = React.useState<boolean>(false);
    const [editDocument, setEditDocument] = React.useState<DocumentItem | undefined>(undefined);
    const [downloadInProgress, setDownloadInProgress] = React.useState<boolean>(false);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    React.useEffect(() => {
        if (documents) {
            let sortedDocuments = [...documents].sort((a: DocumentItem, b: DocumentItem) =>
                a.documentName.localeCompare(b.documentName, undefined, { sensitivity: "base" }),
            );
            setDocumentData(sortedDocuments);
        }
    }, [documents]);

    const enableDisableDocument = (document?: DocumentItem) => {
        if (!document) return;
        setEnableDisableInProgress(true);
        if (document.active) {
            disableDocument(document.documentId)
                .unwrap()
                .then(() => {
                    const eventDetails: AccessEventDocumentDetails = {
                        documentId: document.documentId.toString(),
                        documentTitle: document.documentName,
                    };
                    logEvent("DocumentDisabled", eventDetails);
                    ShowSnackbarElement("Document Disabled", "success");
                })
                .catch(() => {
                    ShowSnackbarElement("Document Disable Failed", "error");
                })
                .finally(() => setEnableDisableInProgress(false));
        } else {
            enableDocument(document.documentId)
                .unwrap()
                .then(() => {
                    const eventDetails: AccessEventDocumentDetails = {
                        documentId: document.documentId.toString(),
                        documentTitle: document.documentName,
                    };
                    logEvent("DocumentEnabled", eventDetails);
                    ShowSnackbarElement("Document Enabled", "success");
                })
                .catch(() => {
                    ShowSnackbarElement("Document Enable Failed", "error");
                })
                .finally(() => setEnableDisableInProgress(false));
        }
    };

    const downloadDocument = async (document?: DocumentItem) => {
        if (!document) return;
        setDownloadInProgress(true);
        try {
            const request: AssetDownloadRequest = {
                fname: document.documentName,
                aikeys: [+document.assetKey],
                flatzip: false,
            };
            await downloadService.DownloadAssets(request);
            const eventDetails: AccessEventDocumentDetails = {
                documentId: document.documentId.toString(),
                documentTitle: document.documentName,
            };
            logEvent("DocumentDownloaded", eventDetails);
            ShowSnackbarElement("Document Downloaded", "success");
        } catch (error) {
            ShowSnackbarElement("Document Download Failed", "error");
        } finally {
            setDownloadInProgress(false);
        }
    };

    const handleDeleteDocument = (document?: DocumentItem) => {
        if (!document) return;
        setDeleteInProgress(true);
        deleteDocument(document.documentId)
            .unwrap()
            .then(() => {
                setDocumentData((prev) => prev.filter((d) => d.documentId !== document.documentId));
                const eventDetails: AccessEventDocumentDetails = {
                    documentId: document.documentId.toString(),
                    documentTitle: document.documentName,
                };
                logEvent("DocumentDeleted", eventDetails);
                ShowSnackbarElement("Document Deleted", "success");
            })
            .catch(() => {
                ShowSnackbarElement("Document Delete Failed", "error");
            })
            .finally(() => setDeleteInProgress(false));
    };

    const uploadDocument = () => {
        setUploadDrawerOpen(true);
    };

    const openEditDocument = (document?: DocumentItem) => {
        if (!document) return;
        setEditDocument(document);
        setEditDrawerOpen(true);
    };

    const handleEditSaveComplete = (documentId: number, newTitle: string) => {
        setDocumentData((prev) =>
            prev
                .map((d) => (d.documentId === documentId ? { ...d, documentName: newTitle } : d))
                .sort((a, b) =>
                    a.documentName.localeCompare(b.documentName, undefined, {
                        sensitivity: "base",
                    }),
                ),
        );
        const eventDetails: AccessEventDocumentDetails = {
            documentId: documentId.toString(),
            documentTitle: newTitle,
        };
        logEvent("DocumentEdited", eventDetails);
        ShowSnackbarElement("Document Edited", "success");
    };

    const handleUploadComplete = (document: DocumentItem) => {
        setDocumentData((prev) =>
            [...prev, document].sort((a, b) =>
                a.documentName.localeCompare(b.documentName, undefined, { sensitivity: "base" }),
            ),
        );
    };

    const breadcrumbs = (
        <SimpleBreadcrumbs>
            <Typography variant="h6" color="text.primary">
                Documents
            </Typography>
        </SimpleBreadcrumbs>
    );

    return (
        <StandardLayout breadcrumbs={breadcrumbs}>
            <React.Fragment>
                <Stack height={1} width={1}>
                    <Card
                        sx={{
                            width: 1,
                            height: 1,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {showSnackbar ? (
                            <CclAlertSnackbar
                                open={true}
                                message={snackbarMessage}
                                severity={snackbarSeverity}
                                onClose={() => setShowSnackbar(false)}
                            />
                        ) : null}
                        <DocumentsDataGridPro
                            documents={documentData}
                            loading={isLoading}
                            uploadDocument={uploadDocument}
                            editDocument={openEditDocument}
                            downloadDocument={downloadDocument}
                            downloadInProgress={downloadInProgress}
                            enableDisableDocument={enableDisableDocument}
                            enableDisableInProgress={enableDisableInProgress}
                            deleteDocument={handleDeleteDocument}
                            deleteInProgress={deleteInProgress}
                        />
                    </Card>
                </Stack>
                {uploadDrawerOpen ? (
                    <DocumentUploadDrawer
                        open={true}
                        onClose={() => setUploadDrawerOpen(false)}
                        onUploadComplete={handleUploadComplete}
                    />
                ) : null}
                {editDrawerOpen ? (
                    <EditDocumentDrawer
                        open={true}
                        document={editDocument}
                        onClose={() => setEditDrawerOpen(false)}
                        onSaveComplete={handleEditSaveComplete}
                    />
                ) : null}
            </React.Fragment>
        </StandardLayout>
    );
};

export default Documents;
